import { useFieldApi } from 'informed';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { correctEmailDomain, DOMAINS_OPTIONS } from './helpers';
import { Input } from './Input';
import { OptionsBlock } from './primitives/SelectBlock';
import { ThemeMode } from '@/helpers/enums';
import { mailMask } from '@/helpers/masks';
import { validateEmail } from '@/helpers/validators';

import { ScLink } from './groups/styled';
import { typography } from '@/styles/helpers';

export const InputEmail = () => {
  const { t } = useTranslation('common');
  const { infoIcon: InfoIcon } = useTheme();

  const { getValue, setValue, validate, getTouched } = useFieldApi('email');

  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [correctionWarning, setCorrectionWarning] = useState<
    string | undefined
  >();

  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onFocus = () => setFocused(true);
  const onBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setFocused(false);
    }, 300);

    handleValidateCorrection();
  };

  const handleValidateCorrection = () => {
    const value = getValue() as string;

    const correctedEmail = correctEmailDomain(value ?? '');
    setCorrectionWarning(correctedEmail);
  };

  const handleCorrectValue = () => {
    if (!correctionWarning) return;

    handleSetValue(correctionWarning);
    validate();
  };

  const handleSetValue = (value: string) => {
    setValue(value);
    setInputValue(value);
    setFocused(false);
    setCorrectionWarning('');
  };

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!focused && getTouched()) validate();
  }, [focused, getTouched, validate]);

  const options = useMemo(() => {
    const inputValueParts = inputValue.replace(/@+/, '@').split('@');
    const inputValueDomain = inputValueParts[1];

    return DOMAINS_OPTIONS.filter((domain) => {
      return domain !== inputValueDomain && domain.startsWith(inputValueDomain);
    }).map((domain) => {
      const value = `${inputValueParts[0]}@${domain}`;
      return { name: value, value };
    });
  }, [inputValue]);

  const isOptionsVisible = useMemo(() => {
    const specialSymbolCount = (inputValue.match(/@/g) || []).length;
    return (
      specialSymbolCount < 2 &&
      inputValue.includes('@') &&
      focused &&
      !!options.length
    );
  }, [focused, inputValue, options]);

  return (
    <ScInputEmail>
      <Input
        autoComplete="off"
        placeholder={t('auth.registration.form.email')}
        validateOn="submit"
        name="email"
        validate={validateEmail}
        parser={mailMask}
        onChange={({ target }) => setInputValue(target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {isOptionsVisible && (
        <div className="options">
          <OptionsBlock
            options={options}
            onChange={(provider) => handleSetValue(provider as string)}
            mode={ThemeMode.Light}
          />
        </div>
      )}
      {correctionWarning && (
        <div className="correctionWarning">
          <InfoIcon />
          <p>
            <Trans
              ns="common"
              values={{ domain: correctionWarning }}
              i18nKey="auth.registration.form.emailCorrection"
              components={{
                btn: <ScLink className="link" onClick={handleCorrectValue} />,
                b: <strong />,
              }}
            />
          </p>
        </div>
      )}
    </ScInputEmail>
  );
};

export const ScInputEmail = styled.div`
  position: relative;

  & .options {
    position: absolute;
    z-index: 6;
    left: 0;
    right: 0;
  }

  & .correctionWarning {
    display: flex;
    padding: 4px 12px;
    margin-top: 4px;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    border-radius: 16px;
    background: rgba(79, 121, 171, 0.2);
    min-height: 32px;

    .link {
      text-decoration: underline;
      ${typography['caption1bold']};
    }

    p {
      ${typography['caption1regular']};
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;
